import React, { useState, useEffect } from 'react';
import { hrmAPI } from '../../lib/api';
import { Plus, RefreshCw, Search, Edit, Trash2 } from 'lucide-react';

const LeaveApplications = () => {
    const [applications, setApplications] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [leaveTypes, setLeaveTypes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [editingId, setEditingId] = useState(null);
    
    const [formData, setFormData] = useState({
        employee_id: '',
        leave_type_id: '',
        start_date: '',
        end_date: '',
        reason: '',
        status: 'pending'
    });

    useEffect(() => { fetchData(); }, []);

    const fetchData = async () => {
        try {
            setLoading(true);
            const [appRes, empRes, typeRes] = await Promise.all([
                hrmAPI.getLeaveApplications(),
                hrmAPI.getEmployees(),
                hrmAPI.getLeaveTypes()
            ]);
            setApplications(appRes.data.data || appRes.data);
            setEmployees(empRes.data.data || empRes.data);
            setLeaveTypes(typeRes.data.data || typeRes.data);
        } catch (err) { setError('Failed to load data'); }
        finally { setLoading(false); }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (editingId) {
                await hrmAPI.updateLeaveApplication(editingId, formData);
            } else {
                await hrmAPI.createLeaveApplication(formData);
            }
            setShowModal(false);
            setEditingId(null);
            setFormData({ employee_id: '', leave_type_id: '', start_date: '', end_date: '', reason: '', status: 'pending' });
            fetchData();
        } catch (err) { setError(err.response?.data?.message || 'Failed to save'); }
    };

    const handleEdit = (app) => {
        setEditingId(app.id);
        setFormData({
            employee_id: app.employee_id,
            leave_type_id: app.leave_type_id,
            start_date: app.start_date,
            end_date: app.end_date,
            reason: app.reason || '',
            status: app.status
        });
        setShowModal(true);
    };

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this leave application?')) {
            try {
                await hrmAPI.deleteLeaveApplication(id);
                fetchData();
            } catch (err) {
                setError(err.response?.data?.message || 'Failed to delete');
            }
        }
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setEditingId(null);
        setFormData({ employee_id: '', leave_type_id: '', start_date: '', end_date: '', reason: '', status: 'pending' });
    };

    const getEmployeeName = (id) => employees.find(e => e.id === id)?.name || '-';
    const getLeaveTypeName = (id) => leaveTypes.find(t => t.id === id)?.name || '-';

    const filteredApplications = applications.filter(app => getEmployeeName(app.employee_id).toLowerCase().includes(searchTerm.toLowerCase()));

    const getStatusColor = (status) => {
        if (status === 'approved') return 'bg-green-100 text-green-800';
        if (status === 'rejected') return 'bg-red-100 text-red-800';
        return 'bg-yellow-100 text-yellow-800';
    };

    return (
        <div>
            <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between">
                <div>
                    <h1 className="text-2xl font-semibold text-gray-800 dark:text-gray-100">Leave Applications</h1>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Manage employee leave requests</p>
                </div>
                <button onClick={() => setShowModal(true)} className="mt-4 sm:mt-0 inline-flex items-center px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg">
                    <Plus className="w-4 h-4 mr-2" /> New Application
                </button>
            </div>

            {error && <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded">{error}</div>}

            <div className="mb-4 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input type="text" placeholder="Search..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-gray-800 dark:text-gray-200" />
            </div>

            <div className="bg-white dark:bg-slate-700 rounded-lg shadow overflow-hidden">
                {loading ? (
                    <div className="p-8 text-center"><RefreshCw className="w-8 h-8 animate-spin mx-auto text-indigo-600" /></div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-gray-50 dark:bg-slate-600">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Employee</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Leave Type</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Start Date</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">End Date</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Status</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 dark:divide-slate-600">
                                {filteredApplications.length > 0 ? filteredApplications.map(app => (
                                    <tr key={app.id} className="hover:bg-gray-50 dark:hover:bg-slate-600">
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-800 dark:text-gray-200">{getEmployeeName(app.employee_id)}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">{getLeaveTypeName(app.leave_type_id)}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">{app.start_date}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">{app.end_date}</td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(app.status)}`}>
                                                {app.status}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                                            <button onClick={() => handleEdit(app)} className="text-indigo-600 hover:text-indigo-900 mr-3"><Edit className="w-4 h-4" /></button>
                                            <button onClick={() => handleDelete(app.id)} className="text-red-600 hover:text-red-900"><Trash2 className="w-4 h-4" /></button>
                                        </td>
                                    </tr>
                                )) : (
                                    <tr><td colSpan="6" className="px-6 py-8 text-center text-gray-500 dark:text-gray-400">No applications found</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {showModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white dark:bg-slate-700 rounded-lg p-6 w-full max-w-md mx-4">
                        <h2 className="text-xl font-semibold mb-4 text-gray-800 dark:text-gray-100">{editingId ? 'Edit Leave Application' : 'New Leave Application'}</h2>
                        <form onSubmit={handleSubmit}>
                            <div className="mb-3">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Employee</label>
                                <select value={formData.employee_id} onChange={(e) => setFormData({...formData, employee_id: e.target.value})} className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 text-gray-800 dark:text-gray-200" required>
                                    <option value="">Select Employee</option>
                                    {employees.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
                                </select>
                            </div>
                            <div className="mb-3">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Leave Type</label>
                                <select value={formData.leave_type_id} onChange={(e) => setFormData({...formData, leave_type_id: e.target.value})} className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 text-gray-800 dark:text-gray-200" required>
                                    <option value="">Select Leave Type</option>
                                    {leaveTypes.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                                </select>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="mb-3"><label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Start Date</label>
                                    <input type="date" value={formData.start_date} onChange={(e) => setFormData({...formData, start_date: e.target.value})} className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 text-gray-800 dark:text-gray-200" required /></div>
                                <div className="mb-3"><label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">End Date</label>
                                    <input type="date" value={formData.end_date} onChange={(e) => setFormData({...formData, end_date: e.target.value})} className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 text-gray-800 dark:text-gray-200" required /></div>
                            </div>
                            <div className="mb-3">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Reason</label>
                                <textarea value={formData.reason} onChange={(e) => setFormData({...formData, reason: e.target.value})} className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 text-gray-800 dark:text-gray-200" rows="3" />
                            </div>
                            {editingId && (
                                <div className="mb-3">
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Status</label>
                                    <select value={formData.status} onChange={(e) => setFormData({...formData, status: e.target.value})} className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 text-gray-800 dark:text-gray-200">
                                        <option value="pending">Pending</option>
                                        <option value="approved">Approved</option>
                                        <option value="rejected">Rejected</option>
                                    </select>
                                </div>
                            )}
                            <div className="flex justify-end gap-3 mt-4">
                                <button type="button" onClick={handleCloseModal} className="px-4 py-2 border rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50">Cancel</button>
                                <button type="submit" className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg">Submit</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default LeaveApplications;
